import {
  Block,
  ComponentBlock,
  ListBlock,
  ListView,
  MediaBlock,
  isCommentBlock,
  isComponentBlock,
  isInstanceBlock,
  isListBlock,
  isMediaBlock,
  normalizeStringPath,
} from '@basehub/data/replicache/index'
import { WriteTransaction } from 'replicache'
import { invariant } from '@epic-web/invariant'
import { AtLeast, createBlock, deleteBlock, updateBlock } from './crud'
import { reorderBlocks } from './reorder'
import { getIdempotentId } from '@basehub/utils/id/index'
import { WithServerSecret } from '@basehub/data/replicache/tx/spec'
import { hasAccessOrThrow } from '@basehub/data/replicache/mutators/team-space/team/auth'
import { getViewDefaultTitle } from '@basehub/data/replicache/schema/block/list/utils'
import {
  Position_Array,
  insertIntoArray,
} from '@basehub/utils/array/insert-into-array'
import { idToSourceBlockId } from './copy-on-write'
import { BlockBatchMutationParams } from './batch'
import {
  BaseHubTransaction,
  blockBaseOps,
  ensureBaseHubTransaction,
} from '@basehub/data/replicache/tx/iso'
import { Position } from '@basehub/data/replicache/mutators/repo-space/block/helpers/fractional-indexing'

/* -------------------------------------------------------------------------------------------------
 * Change List Template
 * -----------------------------------------------------------------------------------------------*/

export type ChangeListTemplateParams = {
  id: string
  newTargetId: string
  isoNow: string
  idSeed: string
  path: string
}

export const changeListTemplate = async (
  tx: WriteTransaction,
  {
    id,
    newTargetId,
    idSeed,
    isoNow,
    path,
  }: WithServerSecret<ChangeListTemplateParams>
) => {
  ensureBaseHubTransaction(tx)
  hasAccessOrThrow({
    serverOnly: tx.serverOnly,
    clientOnly: tx.clientOnly,
    serverSecret: tx.serverSecret,
    allowedRoles: ['owner', 'member'],
    guestAccess: 'branch-author',
  })

  const normalizedPath = normalizeStringPath({
    path,
    edgeId: id,
    format: 'array',
    includeEdge: true,
  })
  const rootBlockId = normalizedPath[0]
  invariant(rootBlockId, 'rootBlockId not found')

  const treeManager = await tx.getTreeManager(rootBlockId)
  const listBlockNode = treeManager.getNode(id)
  const newTargetBlockNode = treeManager.getNode(newTargetId)
  const listBlock = listBlockNode?.value

  const undo: BlockBatchMutationParams['mutations'] = []

  invariant(
    listBlockNode &&
      listBlock &&
      isListBlock(listBlockNode.value) &&
      isListBlock(listBlock),
    'listBlock was not found, or is invalid block type'
  )
  invariant(
    newTargetBlockNode && isComponentBlock(newTargetBlockNode.value),
    'newTargetBlock was not found, or is invalid block type'
  )

  const currentTargetBlockNode = treeManager.getNode(
    listBlockNode.value.typeOptions.targetBlockId
  )
  invariant(
    currentTargetBlockNode && isComponentBlock(currentTargetBlockNode.value),
    'currentTargetBlock was not found, or is invalid block type'
  )

  const instancesInListLength = await getRowsLength({
    listBlock,
    rootBlockId,
    getBlockChildren: tx.getBlockChildren,
    targetBlockId: currentTargetBlockNode.value.id,
  })

  // if collection has instances in it, always throw
  // if it's not nested below a collection, we'll leave it there
  // if component is nested below collection and doesn't have any instances, we'll delete it
  // if component is nested below, collection doesn't have instances, but component does have instances (outside)... we reorder the component into the root.

  if (instancesInListLength > 0) {
    throw new Error('Cannot change list template when there are instances')
  }

  // SAME EXACT LOGIC IN resetListTemplate, SO KEEP IN SYNC
  // we wanna check there are no collections that are created based off of this collection
  // which may have rows in there which we don't wanna mess up

  // so we get all instances that have a collection that was "spawned" from this collection
  let rowsInInstancesTargetingCollectionLength = 0
  for (const nId of Array.from(
    treeManager.compoundIndexes
      .get('byInstanceChildTarget')
      ?.get(idToSourceBlockId(listBlock)) ?? []
  )) {
    const n = treeManager.getNode(nId)
    if (!n) continue
    if (!isListBlock(n.value)) continue
    const rowsLength = await getRowsLength({
      rootBlockId,
      listBlock: n.value,
      getBlockChildren: tx.getBlockChildren,
      targetBlockId: currentTargetBlockNode.value.id,
    })

    if (rowsLength) {
      rowsInInstancesTargetingCollectionLength += rowsLength
    }
  }

  if (rowsInInstancesTargetingCollectionLength > 0) {
    throw new Error(
      'Cannot change list template when there are instances targeting it'
    )
  }

  // we do the update, then we keep doing cleanup
  const { undo: undoUpdateBlock } = await updateBlock(tx, {
    id,
    isoNow,
    path,
    strategy: 'merge',
    instanceChildTarget: listBlock.instanceChildTarget,
    data: {
      type: 'list',
      typeOptions: { targetBlockId: newTargetId },
    },
  })
  undo.push(...undoUpdateBlock)

  // if (parentNode && isComponentBlock(parentNode.value)) {
  //   const { undo: undoSyncComponentInstances } =
  //     await syncAllComponentInstances(tx, {
  //       componentBlockId: parentNode.value.id,
  //       rootBlockId,
  //       idSeed: getIdempotentId({
  //         idSeed,
  //         splitPaths: {
  //           newPath: `change-list-template:sync-component-instances:${id}-${parentNode.value.id}`,
  //         },
  //       }),
  //       isoNow,
  //     })
  //   undo.push(...undoSyncComponentInstances)
  // }

  const currentTargetIsNested =
    currentTargetBlockNode.parentKey === listBlock.id

  if (currentTargetIsNested) {
    const instancesOfComponent = []
    for (const nId of Array.from(
      treeManager.compoundIndexes
        .get('componentInstances')
        ?.get(idToSourceBlockId(currentTargetBlockNode.value)) ?? []
    )) {
      const n = treeManager.getNode(nId)
      if (!n) continue
      if (!isInstanceBlock(n.value)) continue
      if (
        n.value.typeOptions.targetBlockId !==
        idToSourceBlockId(currentTargetBlockNode.value)
      ) {
        continue
      }
      instancesOfComponent.push(n)
    }
    if (instancesOfComponent.length > 0) {
      // reorder component into root
      const { undo: undoReorderBlocks } = await reorderBlocks(tx, {
        rootId: rootBlockId,
        idSeed,
        isoNow,
        blockIds: [currentTargetBlockNode.value.id],
        targetBlockId: rootBlockId,
        intendedIndex: 0,
      })
      undo.push(...undoReorderBlocks)
    } else {
      // delete component
      const result = await deleteBlock(tx, {
        id: currentTargetBlockNode.value.id,
        idSeed,
        isoNow,
        path: treeManager.getPath(currentTargetBlockNode.key, {
          required: true,
        }),
        ignoreComponentDeletionCheck: true,
      })
      if (result?.undo) {
        undo.push(...result.undo)
      }
    }
  }

  return { undo }
}

/* -------------------------------------------------------------------------------------------------
 * Reset List Template
 * -----------------------------------------------------------------------------------------------*/

export type ResetListTemplateParams = {
  id: string
  isoNow: string
  idSeed: string
  path: string
}

export const resetListTemplate = async (
  tx: WriteTransaction,
  { id, idSeed, isoNow, path }: WithServerSecret<ResetListTemplateParams>
) => {
  ensureBaseHubTransaction(tx)
  hasAccessOrThrow({
    serverOnly: tx.serverOnly,
    clientOnly: tx.clientOnly,
    serverSecret: tx.serverSecret,
    allowedRoles: ['owner', 'member'],
    guestAccess: 'branch-author',
  })

  const normalizedPath = normalizeStringPath({
    path,
    edgeId: id,
    format: 'array',
    includeEdge: true,
  })
  const rootBlockId = normalizedPath[0]
  invariant(rootBlockId, 'rootBlockId not found')

  const treeManager = await tx.getTreeManager(rootBlockId)
  const listBlockNode = treeManager.getNode(id)
  const listBlock = listBlockNode?.value

  const undo: BlockBatchMutationParams['mutations'] = []

  invariant(
    listBlockNode &&
      listBlock &&
      isListBlock(listBlockNode.value) &&
      isListBlock(listBlock),
    'listBlock was not found, or is invalid block type'
  )

  const currentTargetBlockNode = treeManager.getNode(
    listBlockNode.value.typeOptions.targetBlockId
  )
  invariant(
    currentTargetBlockNode && isComponentBlock(currentTargetBlockNode.value),
    'currentTargetBlock was not found, or is invalid block type'
  )

  const instancesInList = await getRowsLength({
    rootBlockId,
    listBlock: listBlockNode.value,
    targetBlockId: currentTargetBlockNode.value.id,
    getBlockChildren: tx.getBlockChildren,
  })
  const instancesInListLength = instancesInList

  // if collection has instances in it, always throw
  // if current target is nested below, throw as well (as it's already reset)

  if (instancesInListLength > 0) {
    throw new Error('Cannot change list template when there are instances')
  }

  // SAME EXACT LOGIC IN changeListTemplate, SO KEEP IN SYNC
  // we wanna check there are no collections that are created based off of this collection
  // which may have rows in there which we don't wanna mess up

  // so we get all instances that have a collection that was "spawned" from this collection
  let rowsInInstancesTargetingCollectionLength = 0
  for (const nId of Array.from(
    treeManager.compoundIndexes
      .get('byInstanceChildTarget')
      ?.get(idToSourceBlockId(listBlock)) ?? []
  )) {
    const n = treeManager.getNode(nId)
    if (!n) continue
    if (!isListBlock(n.value)) continue
    const rowsLength = await getRowsLength({
      rootBlockId,
      listBlock: n.value,
      getBlockChildren: tx.getBlockChildren,
      targetBlockId: currentTargetBlockNode.value.id,
    })

    if (rowsLength) {
      rowsInInstancesTargetingCollectionLength += rowsLength
    }
  }

  if (rowsInInstancesTargetingCollectionLength > 0) {
    throw new Error(
      'Cannot change list template when there are instances targeting it'
    )
  }
  if (rowsInInstancesTargetingCollectionLength > 0) {
    throw new Error(
      'Cannot change list template when there are instances targeting it'
    )
  }

  const currentTargetIsNested =
    currentTargetBlockNode.parentKey === listBlock.id

  if (currentTargetIsNested) {
    throw new Error('Cannot reset list template when current target is nested')
  }

  // we create component and place it inside list
  const idForNewTarget = getIdempotentId({
    idSeed,
    splitPaths: { newPath: 'reset-list-template' },
  })

  const { undo: undoUpdateBlock } = await updateBlock(tx, {
    id,
    isoNow,
    path,
    strategy: 'merge',
    instanceChildTarget: listBlock.instanceChildTarget,
    data: {
      type: 'list',
      typeOptions: {
        targetBlockId: idForNewTarget,
      },
    },
  })

  undo.push(...undoUpdateBlock)

  const { undo: undoCreateBlock } = await createBlock(tx, {
    data: {
      id: idForNewTarget,
      title: '',
      type: 'component',
      hidden: true,
    },
    path: normalizedPath.concat(id).join('/'),
    isoNow,
    position: { type: 'first' },
    idSeed: (idSeed = getIdempotentId({ idSeed })),
    parentInstanceChildTarget: listBlock.instanceChildTarget,
  })
  undo.push(...undoCreateBlock)

  return { undo }
}

export type CreateListViewParams = {
  blockId: ListBlock['id']
  targetBlockId_withinTree: ListBlock['typeOptions']['targetBlockId']
  id: ListView['id']
  isoNow: string
  data: Partial<Omit<ListView, 'id'>>
  path: string
  position?: Position_Array
  createDefaultViewIfNotExists?: {
    layout: ListView['layout']
    id: ListView['id']
  }
}

export const createListView = async (
  tx: WriteTransaction,
  {
    blockId,
    targetBlockId_withinTree,
    id,
    isoNow,
    data,
    path,
    position = { type: 'first' },
    createDefaultViewIfNotExists,
  }: CreateListViewParams
): Promise<{ view: ListView; undo: BlockBatchMutationParams['mutations'] }> => {
  ensureBaseHubTransaction(tx)

  const [listBlock, template] = await Promise.all([
    blockBaseOps.get(tx, blockId).then((listBlock) => {
      invariant(listBlock, `List could not be retrieved | id: ${blockId}`)
      invariant(
        isListBlock(listBlock),
        `Provided block id doesn't belong to a list`
      )
      return listBlock
    }),
    blockBaseOps.get(tx, targetBlockId_withinTree).then((template) => {
      invariant(
        template,
        `List template could not be retrieved | id: ${targetBlockId_withinTree}`
      )
      invariant(
        isComponentBlock(template),
        `List template should be a component block, found ${template.type}`
      )
      return template
    }),
  ])

  const normalizedPath = normalizeStringPath({
    path,
    edgeId: id,
    format: 'array',
    includeEdge: true,
  })
  const rootBlockId = normalizedPath[0]
  invariant(rootBlockId, 'rootBlockId not found')

  const templateChildren = await tx.getBlockChildren({
    rootBlockId,
    sourceBlockId: idToSourceBlockId(template),
  })

  const defaultColumnsMetadata = (
    await Promise.all([
      {
        blockId: '__title',
        hidden: false,
        width: 200,
      },
      ...templateChildren.map(async (child) => {
        const c = await blockBaseOps.get(tx, child.id)
        if (!c || isCommentBlock(c)) return

        return { blockId: idToSourceBlockId(c), hidden: false, width: 200 }
      }),
    ])
  ).filter(Boolean)

  const layout = data.layout || 'table'
  const defaultTitle =
    layout.charAt(0).toUpperCase() + layout.slice(1) + ' view'

  const view: ListView = {
    id,
    filter: data?.filter ?? [],
    sort: data?.sort ?? [],
    layout: data?.layout || 'table',
    title: data?.title ?? defaultTitle,
    showOnFirstLoad: data?.showOnFirstLoad ?? 10,
    columnsMetadata: data?.columnsMetadata?.length
      ? data.columnsMetadata
      : defaultColumnsMetadata,
  }

  if (view.layout === 'gallery') {
    view.thumbnailColumn = await getThumbnailCandidateId({
      tx,
      template,
      rootBlockId,
    })
  }

  let defaultView: ListView | undefined
  if (createDefaultViewIfNotExists && !listBlock.value.views?.length) {
    defaultView = {
      id: createDefaultViewIfNotExists.id,
      layout: createDefaultViewIfNotExists.layout,
      title: getViewDefaultTitle({
        layout: createDefaultViewIfNotExists.layout,
      }),
      filter: [],
      sort: [],
      showOnFirstLoad: 10,
      columnsMetadata: defaultColumnsMetadata,
    } satisfies ListView
  }

  const newViews = insertIntoArray({
    array: listBlock.value.views ?? [],
    item: view,
    position,
    getItemId: (v) => v.id,
  })

  const { undo } = await updateBlock(tx, {
    id: blockId,
    instanceChildTarget: listBlock.instanceChildTarget,
    data: {
      type: listBlock.type,
      value: { views: [defaultView, ...newViews].filter(Boolean) },
    },
    isoNow,
    path,
    strategy: 'merge-deep',
  })

  return { view, undo }
}

export type DeleteListViewParams = {
  blockId: ListBlock['id']
  id: ListView['id'] | undefined
  isoNow: string
  path: string
}

export const deleteListView = async (
  tx: WriteTransaction,
  { id, blockId, isoNow, path }: DeleteListViewParams
) => {
  ensureBaseHubTransaction(tx)

  const listBlock = await blockBaseOps.get(tx, blockId)
  invariant(listBlock, `List could not be retrieved | id: ${blockId}`)
  invariant(
    isListBlock(listBlock),
    `Provided block id doesn't belong to a list`
  )

  const newViews = listBlock.value.views?.filter((v) => v.id !== id)
  const { undo } = await updateBlock(tx, {
    id: blockId,
    path,
    instanceChildTarget: listBlock.instanceChildTarget,
    data: {
      type: 'list',
      value: { views: newViews },
    },
    strategy: 'merge-deep',
    isoNow,
  })

  return { newViews, undo }
}

export type UpdateListViewParams = {
  blockId: ListBlock['id']
  targetBlockId_withinTree: ListBlock['typeOptions']['targetBlockId']
  id: ListView['id'] | undefined
  data: Partial<Omit<ListView, 'id'>>
  idSeed: string
  isoNow: string
  path: string
  forceComponentInstancesSync?: boolean
}

export const updateListView = async (
  tx: WriteTransaction,
  {
    id,
    blockId,
    targetBlockId_withinTree,
    data,
    isoNow,
    path,
    idSeed, // in case the view is
    forceComponentInstancesSync = false,
  }: UpdateListViewParams
): Promise<{
  view: ListView | undefined
  undo: BlockBatchMutationParams['mutations']
}> => {
  ensureBaseHubTransaction(tx)

  if (!id) {
    const layout = data.layout || 'table'
    const defaultTitle =
      layout.charAt(0).toUpperCase() + layout.slice(1) + ' view'

    return await createListView(tx, {
      id: (idSeed = getIdempotentId({ idSeed })),
      blockId,
      targetBlockId_withinTree,
      data: { title: data.title || defaultTitle, ...data },
      isoNow,
      path,
    })
  }

  const normalizedPath = normalizeStringPath({
    path,
    edgeId: id,
    format: 'array',
    includeEdge: true,
  })
  const rootBlockId = normalizedPath[0]
  invariant(rootBlockId, 'rootBlockId not found')

  const [listBlock, template] = await Promise.all([
    blockBaseOps.get(tx, blockId).then((listBlock) => {
      invariant(listBlock, `List could not be retrieved | id: ${blockId}`)
      invariant(
        isListBlock(listBlock),
        `Provided block id doesn't belong to a list`
      )
      return listBlock
    }),
    blockBaseOps.get(tx, targetBlockId_withinTree).then((template) => {
      invariant(
        template,
        `List template could not be retrieved | id: ${targetBlockId_withinTree}`
      )
      invariant(
        isComponentBlock(template),
        `List template should be a component block, found ${template.type}`
      )
      return template
    }),
  ])

  let view: ListView | undefined = undefined

  const newViewsPromises = (listBlock.value.views ?? []).map(async (v) => {
    if (v.id !== id) return v
    const updatedView: ListView = { ...v, ...data }

    if (v.layout !== 'gallery' && updatedView.layout === 'gallery') {
      const thumbnailCandidateId = await getThumbnailCandidateId({
        tx,
        template,
        rootBlockId,
      })

      updatedView.thumbnailColumn = thumbnailCandidateId
    }

    if (v.layout !== 'inline' && updatedView.layout === 'inline') {
      updatedView.thumbnailColumn = '__title'
    }

    view = updatedView
    return updatedView
  })

  const newViews = await Promise.all(newViewsPromises)

  const { undo } = await updateBlock(tx, {
    id: blockId,
    path,
    instanceChildTarget: listBlock.instanceChildTarget,
    data: { type: 'list', value: { views: newViews } },
    strategy: 'merge-deep',
    isoNow,
    ignoreHashComparison: true,
    forceComponentInstancesSync: forceComponentInstancesSync
      ? { idSeed: (idSeed = getIdempotentId({ idSeed })) }
      : undefined,
  })

  return { view, undo }
}

const getThumbnailCandidateId = async ({
  tx,
  template,
  rootBlockId,
}: {
  tx: BaseHubTransaction
  template: ComponentBlock
  rootBlockId: string
}) => {
  let thumbnailCandidateId: MediaBlock['id'] | undefined

  const templateChildren = await tx.getBlockChildren({
    rootBlockId,
    sourceBlockId: idToSourceBlockId(template),
  })

  const idealTitleKeywords = new Set([
    'thumbnail',
    'cover',
    'poster',
    'image',
    'picture',
    'photo',
    'icon',
    'logo',
    'brand',
  ])

  // Process children sequentially to allow early return
  for (const child of templateChildren) {
    const childBlock = await blockBaseOps.get(tx, child.id)

    if (
      !childBlock ||
      !isMediaBlock(childBlock) ||
      childBlock.value.status !== 'uploaded' ||
      !['video', 'image'].includes(childBlock.value.type)
    ) {
      continue
    }

    const blockId = idToSourceBlockId(childBlock)

    // Check for ideal match first
    if (idealTitleKeywords.has(childBlock.title.toLowerCase())) {
      return blockId
    }

    // If this is the first valid candidate, store it
    // but continue looking for an ideal match
    if (!thumbnailCandidateId) {
      thumbnailCandidateId = blockId
    }
  }

  return thumbnailCandidateId
}

export const createColumnAndAddToColumnMetadata = async (
  tx: WriteTransaction,
  {
    listBlockId,
    viewId,
    data,
    targetBlockId_withinTree,
    isoNow,
    path,
    idSeed,
  }: {
    data: AtLeast<Block, 'id' | 'type'>
    listBlockId: ListBlock['id']
    viewId?: ListView['id']
    targetBlockId_withinTree: ListBlock['typeOptions']['targetBlockId']
    isoNow: string
    path: string
    idSeed: string
  }
): Promise<{
  newColumn: Block
  view: ListView | undefined
  undo: BlockBatchMutationParams['mutations']
}> => {
  ensureBaseHubTransaction(tx)

  const undo: BlockBatchMutationParams['mutations'] = []

  const listBlock = await blockBaseOps
    .get(tx, listBlockId)
    .then((listBlock) => {
      invariant(listBlock, `List could not be retrieved | id: ${listBlockId}`)
      invariant(
        isListBlock(listBlock),
        `Provided block id doesn't belong to a list`
      )
      return listBlock
    })

  const normalizedPath = normalizeStringPath({
    path,
    edgeId: listBlockId,
    format: 'array',
    includeRoot: true,
  })
  const rootBlockId = normalizedPath[0]
  invariant(rootBlockId, 'rootBlockId not found')

  const { newBlock: newColumn, undo: undoCreate } = await createBlock(tx, {
    data,
    path,
    position: { type: 'last' },
    isoNow,
    idSeed: (idSeed = getIdempotentId({ idSeed })),
    parentInstanceChildTarget: listBlock.instanceChildTarget,
  })
  undo.push(...undoCreate)

  const listPath = normalizeStringPath({
    path,
    edgeId: listBlockId,
    format: 'string',
    includeEdge: true,
  })

  let listViewData: Partial<ListView> = {}
  if (listBlock.value.views?.length) {
    const view = listBlock.value.views.find((v) => v.id === viewId)
    listViewData = {
      layout: view?.layout,
      columnsMetadata: [
        ...(view?.columnsMetadata ?? []),
        { blockId: idToSourceBlockId(newColumn), hidden: false, width: 200 },
      ],
    }
    if (listViewData.layout === 'gallery' && view?.layout === 'gallery') {
      listViewData.thumbnailColumn = view.thumbnailColumn
      if (!listViewData?.thumbnailColumn && newColumn.type === 'media') {
        listViewData.thumbnailColumn = idToSourceBlockId(newColumn)
      }
    }
  }

  const { view, undo: undoUpdateView } = await updateListView(tx, {
    id: viewId,
    blockId: listBlockId,
    targetBlockId_withinTree,
    data: listViewData,
    isoNow,
    path: listPath,
    idSeed,
  })
  undo.push(...undoUpdateView)

  return { newColumn, view, undo }
}

export type ClearListCellsParams = {
  blockIds: Block['id'][]
  isoNow: string
  rootId: string
}

export async function clearListCells(
  tx: WriteTransaction,
  { blockIds, isoNow, rootId }: ClearListCellsParams
) {
  ensureBaseHubTransaction(tx)
  const treeManager = await tx.getTreeManager(rootId)

  const undo: BlockBatchMutationParams['mutations'] = []

  const blockPaths = blockIds.map((bId) => {
    const block = treeManager.getNode(bId)
    if (!block) throw new Error(`Block with id ${bId} not found`)

    const path = treeManager.getPath(bId, {
      format: 'string',
      required: true,
    })
    return { path, bId }
  })

  const promises = blockPaths.map(async ({ bId, path }) => {
    const block = treeManager.getNode(bId)?.value
    if (!block) return

    const updates: Partial<Block> = {}
    switch (block.type) {
      case 'root':
      case 'document':
      case 'instance':
      case 'component':
      case 'list':
        return
      case 'comment':
      case 'rich-text':
        updates.value = { ...block.value, state: '' }
        break
      case 'number':
      case 'text':
      case 'icon':
      case 'date':
        updates.value = null
        break
      case 'select':
        updates.value = []
        break
      case 'reference':
        updates.value = { ...block.value, targetBlockIds: [] }
        break
      case 'media': {
        updates.value = { status: 'idle' }
        break
      }
      case 'color':
        updates.value = '#000'
        break
      case 'boolean':
        updates.value = false
        break

      case 'code-snippet':
        updates.value = {
          code: '',
          language: block.value.language,
        }
        break
      case 'og-image':
        updates.value = {}
        break

      default:
        return
    }

    const result = await updateBlock(tx, {
      id: bId,
      instanceChildTarget: block.instanceChildTarget,
      data: {
        type: block.type,
        ...(updates as Record<string, unknown>),
      },
      path,
      strategy: 'merge',
      isoNow,
    })
    undo.unshift(...result.undo)
    return result
  })

  await Promise.all(promises)
  return { undo }
}

export const createListChildrenFromFileNames = async (
  tx: WriteTransaction,
  {
    fileNames,
    listBlockId,
    isoNow,
    path,
    idSeed,
    position,
  }: {
    fileNames: string[]
    listBlockId: ListBlock['id']
    isoNow: string
    path: string
    idSeed: string
    position: Position
  }
) => {
  ensureBaseHubTransaction(tx)

  const listBlock = await blockBaseOps
    .get(tx, listBlockId)
    .then((listBlock) => {
      invariant(listBlock, `List could not be retrieved | id: ${listBlockId}`)
      invariant(
        isListBlock(listBlock),
        `Provided block id doesn't belong to a list`
      )

      return listBlock
    })

  const newBlocks: Block[] = []

  for (const fileName of fileNames) {
    const id = (idSeed = getIdempotentId({ idSeed }))

    const { newBlock } = await createBlock(tx, {
      data: {
        id,
        title: fileName,
        type: 'instance',
        typeOptions: {
          targetBlockId: listBlock.typeOptions.targetBlockId,
        },
      },
      parentInstanceChildTarget: listBlock.instanceChildTarget,
      path,
      isoNow,
      position,
      skipRehashing: true,
      idSeed: getIdempotentId({
        idSeed,
        splitPaths: { newPath: id },
      }),
    })

    newBlocks.push(newBlock)
  }

  return newBlocks
}

export const setListChildrenAsUploadingFiles = async (
  tx: WriteTransaction,
  {
    listBlockId,
    isoNow,
    path,
    childIdsCellsMap,
  }: {
    listBlockId: ListBlock['id']
    isoNow: string
    path: string
    childIdsCellsMap: {
      id: Block['id']
      cellId: Block['id']
      targetId: Block['instanceChildTarget']
    }[]
  }
) => {
  ensureBaseHubTransaction(tx)

  const listBlock = await blockBaseOps
    .get(tx, listBlockId)
    .then((listBlock) => {
      invariant(listBlock, `List could not be retrieved | id: ${listBlockId}`)
      invariant(
        isListBlock(listBlock),
        `Provided block id doesn't belong to a list`
      )

      return listBlock
    })

  const normalizedPath = normalizeStringPath({
    path,
    format: 'array',
    includeRoot: true,
  })
  const rootBlockId = normalizedPath[0]
  invariant(rootBlockId, 'rootBlockId not found')

  const listChildren = await tx.getBlockChildren({
    rootBlockId,
    sourceBlockId: idToSourceBlockId(listBlock),
  })

  for (const { id, cellId, targetId } of childIdsCellsMap) {
    // isn't child of this list
    if (!listChildren.some((c) => c.id === id)) continue

    await updateBlock(tx, {
      id: cellId,
      instanceChildTarget: targetId,
      data: { value: { status: 'uploading' } },
      path: path + '/' + id + '/' + cellId,
      strategy: 'merge-deep',
      isoNow,
    })
  }
}

async function getRowsLength({
  rootBlockId,
  getBlockChildren,
  listBlock,
  ignoreTemplateComponent = true,
  targetBlockId,
}: {
  rootBlockId: string
  getBlockChildren: BaseHubTransaction['getBlockChildren']
  listBlock: ListBlock
  ignoreTemplateComponent?: boolean
  targetBlockId: string
}) {
  return (
    await getBlockChildren({
      rootBlockId,
      sourceBlockId: idToSourceBlockId(listBlock),
      includeDeleted: false,
    })
  ).filter(
    (c) =>
      c.blockId !== targetBlockId &&
      (!ignoreTemplateComponent ||
        (c.sourceBlockId ?? c.blockId) !== listBlock.typeOptions.targetBlockId)
  ).length
}
